import jsPDF from 'jspdf';

// Shared PDF constants
export const PDF_FORMAT = 'a4';
export const PDF_QUALITY = 0.92;

/**
 * Extract CSS styles from current page for off-screen rendering.
 * Collects all CSS rules, skipping cross-origin stylesheets.
 * @returns Extracted CSS styles as a string
 */
export function extractCurrentPageStyles(): string {
  const styles: string[] = [];

  try {
    for (const styleSheet of document.styleSheets) {
      try {
        const rules = styleSheet.cssRules || styleSheet.rules;
        if (rules) {
          for (const rule of Array.from(rules)) {
            if (rule.cssText) {
              styles.push(rule.cssText);
            }
          }
        }
      } catch {
        // Skip cross-origin stylesheets
      }
    }
  } catch (error) {
    console.warn('Error extracting styles:', error);
  }

  return styles.join('\n');
}

export const createPDFInstance = (orientation: 'portrait' | 'landscape' = 'portrait'): jsPDF => {
  return new jsPDF({
    orientation,
    unit: 'mm',
    format: PDF_FORMAT,
  });
};

export const getOptimalOrientation = (width: number, height: number): 'portrait' | 'landscape' => {
  return width > height ? 'landscape' : 'portrait';
};

interface ImageDimensions {
  width: number;
  height: number;
  xOffset: number;
  yOffset: number;
}

export const calculateImageDimensions = (
  imageWidth: number,
  imageHeight: number,
  pageWidth: number,
  pageHeight: number
): ImageDimensions => {
  const imgAspectRatio = imageWidth / imageHeight;
  const pdfAspectRatio = pageWidth / pageHeight;

  let width = pageWidth;
  let height = pageHeight;

  if (imgAspectRatio > pdfAspectRatio) {
    // Image is wider than PDF page
    height = pageWidth / imgAspectRatio;
  } else {
    // Image is taller than PDF page
    width = pageHeight * imgAspectRatio;
  }

  // Center the image on the page
  const xOffset = (pageWidth - width) / 2;
  const yOffset = (pageHeight - height) / 2;

  return { width, height, xOffset, yOffset };
};

export const addImageToPDF = (
  pdf: jsPDF,
  imageData: string,
  imageFormat: 'JPEG' | 'PNG',
  imageWidth: number,
  imageHeight: number,
  quality?: number
): void => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const dimensions = calculateImageDimensions(
    imageWidth,
    imageHeight,
    pageWidth,
    pageHeight
  );

  pdf.addImage(
    imageData,
    imageFormat,
    dimensions.xOffset,
    dimensions.yOffset,
    dimensions.width,
    dimensions.height,
    undefined,
    quality ? 'FAST' : undefined
  );
};

export const canvasToPDFFile = (
  canvas: HTMLCanvasElement,
  filename: string,
  quality: number = PDF_QUALITY
): File => {
  const orientation = getOptimalOrientation(canvas.width, canvas.height);
  const pdf = createPDFInstance(orientation);

  const imageData = canvas.toDataURL('image/jpeg', quality);
  addImageToPDF(pdf, imageData, 'JPEG', canvas.width, canvas.height);

  const pdfBlob = pdf.output('blob');
  return new File([pdfBlob], filename, {
    type: 'application/pdf',
    lastModified: Date.now(),
  });
};
